import i18n from "./i18n/i18n"
import { openForm } from "./sentryFeedbackPriority"

// Sentry's form takes one picture: a screenshot of the page, and only on a desktop (the SDK leaves the
// button out on phones and tablets). A photo from the site, a screenshot or a short video taken on the
// phone, the PDF that came out wrong had no way in. So, like the priority select, a button is put into the
// form each time it opens: it picks files from the device, shows them under it with a remove button each,
// and they go out as attachments of the feedback event, after the SDK's screenshot. Sentry's feedback view
// shows every attachment in its screenshot gallery, and one it cannot show as an image as a download link.
//
// This leans on the SDK's markup (the `#sentry-feedback` host, `dialog[open]`, `.form__top`, the `form__*`
// and `btn` classes). If an SDK update changes it, the button is simply not added and feedback arrives
// with the screenshot alone — worth checking after bumping @sentry/vue.

const MB = 1024 * 1024
// The SDK stops waiting for Sentry's answer after 30 seconds and then asks for the report to be sent again,
// while the upload goes on and usually arrives: a second one is a duplicate, and over the same connection
// it runs out of time as well. 10 MB (84 Mbit) still makes it over 3 Mbit/s of upload, a weak mobile
// connection on a site. Sentry itself would take 200 MB per event, but every byte counts against the
// attachment quota.
const MAX_TOTAL_BYTES = 10 * MB
// A phone photo is 3–8 MB, so an image over SHRINK_ABOVE is scaled to MAX_EDGE on its longer side and sent
// as JPEG, which keeps it around 1 MB (and leaves its EXIF data, the location among it, behind). Smaller
// ones, mostly screenshots, go as they are, sharp. A GIF is left alone: shrinking would keep its first frame
// only. Videos go as they are too; the browser has no encoder to shrink them without a large library.
const SHRINK_ABOVE = 2 * MB
const MAX_EDGE = 2560
const JPEG_QUALITY = 0.85

const t = (key, values) => i18n.global.t(`general.sentry.attachments.${key}`, values)

/** Per form, its files in the order picked: `{ attachment, reading }`, `attachment` null until read. */
const pickedFiles = new WeakMap()

const STYLE_ID = "lew-feedback-attachments-style"

const STYLE = `
.lew-attachments {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.lew-attachments__list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.lew-attachments__list:empty {
  display: none;
}
.lew-attachments__item {
  position: relative;
  width: 56px;
  height: 56px;
  box-sizing: border-box;
  border: var(--border);
  border-radius: 6px;
  overflow: hidden;
  background: color-mix(in srgb, currentColor 8%, transparent);
}
.lew-attachments__item img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.lew-attachments__file {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 2px;
  height: 100%;
  box-sizing: border-box;
  padding: 4px;
  font-size: 9px;
  line-height: 1.2;
}
.lew-attachments__file strong {
  font-size: 12px;
}
.lew-attachments__file span {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  -webkit-hyphens: auto;
  hyphens: auto;
  overflow-wrap: anywhere;
}
.lew-attachments__remove {
  position: absolute;
  top: 2px;
  right: 2px;
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font: inherit;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
}
.lew-attachments__remove:hover {
  background: rgba(0, 0, 0, 0.8);
}
.lew-attachments__remove:focus-visible {
  outline: var(--outline);
}
`

function element(tag, className, text) {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text) node.textContent = text
  return node
}

function addStyle(shadow) {
  if (shadow.getElementById(STYLE_ID)) return
  const style = element("style")
  style.id = STYLE_ID
  style.textContent = STYLE
  shadow.append(style)
}

const isShrinkable = file => file.type.startsWith("image/") && file.type !== "image/gif"

/** Scaled to MAX_EDGE on its longer side, as JPEG; null if the canvas cannot encode it. */
async function shrink(file) {
  const url = URL.createObjectURL(file)
  const canvas = element("canvas")
  try {
    const image = new Image()
    image.src = url
    // Rejects for a format this browser cannot decode. An <img> stands a photo upright by its EXIF
    // orientation, and drawImage draws it that way.
    await image.decode()
    const scale = Math.min(1, MAX_EDGE / Math.max(image.naturalWidth, image.naturalHeight))
    canvas.width = Math.round(image.naturalWidth * scale)
    canvas.height = Math.round(image.naturalHeight * scale)
    const context = canvas.getContext("2d")
    // JPEG has no transparency: a transparent screenshot would turn black.
    context.fillStyle = "#fff"
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(image, 0, 0, canvas.width, canvas.height)
  } finally {
    URL.revokeObjectURL(url)
  }
  return new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY))
}

/**
 * The attachment and the blob it was read from, if it fits into the `room` left; rejects with the
 * problem's wording when it cannot go.
 */
async function read(file, room) {
  const tooLarge = () => new Error(t("tooLarge", { name: file.name, max: MAX_TOTAL_BYTES / MB }))
  // Not read at all when even shrinking could not make it fit: a long video would sit in memory for nothing.
  if (!isShrinkable(file) && file.size > room) throw tooLarge()
  const large = isShrinkable(file) && file.size > SHRINK_ABOVE
  const shrunk = large ? await shrink(file).catch(() => null) : null
  // As picked when shrinking failed (a format this browser cannot decode) or did not help.
  const blob = shrunk && shrunk.size < file.size ? shrunk : file
  if (blob.size > room) throw tooLarge()
  const data = await blob.arrayBuffer().catch(() => {
    // Moved or deleted since it was picked, or a cloud file not on the device.
    throw new Error(t("unreadable", { name: file.name }))
  })
  const attachment = {
    filename: blob === file ? file.name : `${file.name.replace(/\.[^.]*$/, "")}.jpg`,
    data: new Uint8Array(data),
    contentType: blob.type || "application/octet-stream",
  }
  return { attachment, blob }
}

/** What stands in for a file that is not shown as an image: its type, then its name without it. */
function fileTile(name) {
  const tile = element("span", "lew-attachments__file")
  const [, base, extension] = /^(.+)\.([^.]{1,5})$/.exec(name) ?? [null, name]
  if (extension) tile.append(element("strong", null, extension.toUpperCase()))
  tile.append(element("span", null, base))
  return tile
}

function showPreview(item, blob, name) {
  if (!blob.type.startsWith("image/")) {
    item.prepend(fileTile(name))
    return
  }
  const image = element("img")
  image.alt = name
  const url = URL.createObjectURL(blob)
  // Shown from a blob, which the browser can let go once it is decoded.
  image.addEventListener("load", () => URL.revokeObjectURL(url), { once: true })
  // Not decodable here (HEIC outside Safari): it still goes, so it shows like any other file.
  image.addEventListener(
    "error",
    () => {
      URL.revokeObjectURL(url)
      image.replaceWith(fileTile(name))
    },
    { once: true }
  )
  image.src = url
  item.prepend(image)
}

// `feedbackIntegration({ onFormOpen })`: runs right after the form has rendered.
export function addAttachmentPicker() {
  const form = openForm()
  const top = form?.querySelector(".form__top")
  // A form reopened after Abbrechen still has its files, and keeps them like it keeps the text.
  if (!top || pickedFiles.has(form)) return
  const files = []
  pickedFiles.set(form, files)
  addStyle(form.getRootNode())

  const button = element("button", "btn btn--default", t("attach"))
  button.type = "button"
  const input = element("input")
  input.type = "file"
  input.multiple = true
  input.hidden = true
  const list = element("ul", "lew-attachments__list")
  const problems = element("div", "form__error-container")
  problems.hidden = true
  const section = element("div", "lew-attachments")
  section.append(button, input, list, problems)
  // Last, under the screenshot button. Preact leaves nodes it did not render where they are, as with the
  // priority select.
  top.append(section)

  const problem = text => {
    problems.append(element("div", null, text))
    problems.hidden = false
  }
  const clearProblems = () => {
    problems.replaceChildren()
    problems.hidden = true
  }
  const used = () => files.reduce((sum, file) => sum + (file.attachment?.data.byteLength ?? 0), 0)
  // One file at a time, in the order picked, so which one no longer fits does not depend on which was
  // read first.
  let queue = Promise.resolve()

  const pick = picked => {
    clearProblems()
    for (const file of picked) {
      const entry = { attachment: null }
      files.push(entry)
      const item = element("li", "lew-attachments__item")
      item.title = file.name
      const remove = element("button", "lew-attachments__remove", "×")
      remove.type = "button"
      remove.setAttribute("aria-label", t("remove"))
      item.append(remove)
      list.append(item)

      /** Whether it was still picked. */
      const drop = () => {
        item.remove()
        const index = files.indexOf(entry)
        if (index < 0) return false
        files.splice(index, 1)
        return true
      }
      remove.addEventListener("click", () => {
        drop()
        // The room it took is free again, and the rest were about other files.
        clearProblems()
      })
      entry.reading = queue
        .then(() => read(file, MAX_TOTAL_BYTES - used()))
        .then(({ attachment, blob }) => {
          entry.attachment = attachment
          showPreview(item, blob, file.name)
        })
      queue = entry.reading.catch(() => {})
      entry.reading.catch(error => {
        if (drop()) problem(error.message)
      })
    }
  }

  button.addEventListener("click", () => input.click())
  input.addEventListener("change", () => {
    const picked = [...input.files]
    // So the same file can be picked again after it was removed.
    input.value = ""
    pick(picked)
  })
  holdSubmitWhileReading(form, files)
}

// The files are read when they are picked, because `beforeSendFeedback` cannot wait. A send pressed while
// one is still being read (a large photo on a slow phone) is held here, in the capture phase on the dialog,
// before the SDK's handler on the form sees it, and repeated once they are read. If one of them cannot go,
// its problem shows under the button and the person decides whether to send without it.
function holdSubmitWhileReading(form, files) {
  let holding = false
  form.closest("dialog")?.addEventListener(
    "submit",
    event => {
      const reading = files.filter(file => !file.attachment).map(file => file.reading)
      if (!holding && !reading.length) return
      event.preventDefault()
      event.stopPropagation()
      if (holding) return
      holding = true
      Promise.all(reading).then(
        () => {
          holding = false
          form.requestSubmit()
        },
        () => {
          holding = false
        }
      )
    },
    true
  )
}

// `client.on("beforeSendFeedback")`: fires synchronously while the form is still open and submitting, with
// the hint the SDK then captures the event with. Its `attachments` hold the screenshot, if one was taken.
export function attachFiles(_event, hint) {
  const attachments = pickedFiles.get(openForm())?.flatMap(file => file.attachment ?? []) ?? []
  if (hint && attachments.length) hint.attachments = [...(hint.attachments ?? []), ...attachments]
}
